import React, { useState, useRef, ChangeEvent, DragEvent } from 'react';
import axios from 'axios';
import './ModernFileUploader.css';

interface FileUploaderProps {
  id: string;
  label: string;
  onChange: (file: File, fileUrl: string) => void;
  required?: boolean;
  error?: string;
  accept?: string;
}

const ModernFileUploader: React.FC<FileUploaderProps> = ({
  id,
  label,
  onChange,
  required = false,
  error,
  accept = "image/*",
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isCompressing, setIsCompressing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  // Image compression function with progressive quality reduction
  const compressImage = async (file: File, maxWidth: number = 2000, maxHeight: number = 2000): Promise<File> => {
    const maxSize = 10 * 1024 * 1024; // 10MB target
    const qualities = [0.8, 0.6, 0.4, 0.2]; // Progressive quality reduction
    
    for (const quality of qualities) {
      const compressedFile = await compressImageWithQuality(file, maxWidth, maxHeight, quality);
      
      // If compressed file is small enough, return it
      if (compressedFile.size <= maxSize) {
        return compressedFile;
      }
      
      // If we're at the lowest quality and still too large, try smaller dimensions
      if (quality === 0.2 && compressedFile.size > maxSize) {
        const smallerFile = await compressImageWithQuality(file, Math.floor(maxWidth * 0.7), Math.floor(maxHeight * 0.7), quality);
        if (smallerFile.size <= maxSize) {
          return smallerFile;
        }
      }
    }
    
    // If all attempts fail, return the best compressed version
    return await compressImageWithQuality(file, maxWidth, maxHeight, 0.2);
  };

  // Helper function for single compression attempt
  const compressImageWithQuality = (file: File, maxWidth: number, maxHeight: number, quality: number): Promise<File> => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Calculate new dimensions while maintaining aspect ratio
        let { width, height } = img;
        
        if (width > height) {
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
        }
        
        // Set canvas dimensions
        canvas.width = width;
        canvas.height = height;
        
        // Draw and compress
        ctx?.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob(
          (blob) => {
            if (blob) {
              // Create a new file with the compressed blob
              const compressedFile = new File([blob], file.name, {
                type: file.type === 'image/png' ? 'image/jpeg' : file.type, // Convert PNG to JPEG for better compression
                lastModified: Date.now(),
              });
              resolve(compressedFile);
            } else {
              resolve(file); // Fallback to original if compression fails
            }
          },
          file.type === 'image/png' ? 'image/jpeg' : file.type, // Convert PNG to JPEG
          quality
        );
      };
      
      img.onerror = () => {
        resolve(file); // Fallback to original if image load fails
      };
      
      img.src = URL.createObjectURL(file);
    });
  };

  const handleFileSelect = async (file: File) => {
    setSelectedFile(file);
    
    let fileToUpload = file;
    
    // Check if it's an image and compress it
    if (file.type.startsWith('image/')) {
      setIsCompressing(true);
      try {

        fileToUpload = await compressImage(file, 2000, 2000);

        // Show compression results to user
        const compressionRatio = ((1 - fileToUpload.size / file.size) * 100).toFixed(1);

      } catch (error) {

        // Use original file if compression fails
        fileToUpload = file;
      } finally {
        setIsCompressing(false);
      }
    }
    
    // Start the file upload with the (possibly compressed) file
    uploadFile(fileToUpload);
  };

  const uploadFile = async (file: File) => {
    // Check file size before upload
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    if (file.size > maxSize) {

      alert('فایل پس از فشرده‌سازی هنوز بزرگ است. لطفا فایل کوچکتری انتخاب کنید.');
      setSelectedFile(null);
      return;
    }
    
    const formData = new FormData();
    formData.append('file', file);
    
    setIsUploading(true);
    setProgress(0);
    
    try {
      // Import API config
      const API_CONFIG = (await import('../../config/api')).default;
      
      // Get token from local storage
      const token = localStorage.getItem('authToken');
      
      // Use API_CONFIG to get the correct URL
      const uploadUrl = API_CONFIG.getUrl('uploads/file');
      
      const response = await axios.post(uploadUrl, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': token ? `Bearer ${token}` : '',
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setProgress(percentCompleted);
          }
        }
      });
      
      if (response.data.success) {

        // Use the uploadURL from API_CONFIG which is the correct base URL for uploads
        const serverUrl = `${API_CONFIG.uploadURL}${response.data.file.url.replace(/^\//, '')}`;

        setPreviewUrl(serverUrl);
        
        // Call the parent component's onChange handler with file and URL
        onChange(file, response.data.file.url);
      }
    } catch (error) {

      if (axios.isAxiosError(error) && error.response) {
        const errorMessage = error.response.data?.message || 'خطا در آپلود فایل';
        alert(`خطا در آپلود: ${errorMessage}`);
      } else {
        alert('خطا در آپلود فایل. لطفا دوباره تلاش کنید.');
      }
      setSelectedFile(null);
    } finally {
      setIsUploading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedFile(null);
    setPreviewUrl(null);
    setProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="mb-4">
      <label htmlFor={id} className="block mb-1 font-medium text-right">
        {label}
        {required && <span className="text-red-500 mr-1">*</span>}
      </label>
      
              <div
        className={`border-2 border-dashed rounded-md p-4 text-center cursor-pointer transition-all
          ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-slate-300 hover:border-blue-400'}
          ${isUploading || isCompressing ? 'pointer-events-none' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <input
          type="file"
          id={id}
          ref={fileInputRef}
          className="hidden"
          onChange={handleInputChange}
          accept={accept}
          data-error={id}
        />
        
        {isCompressing ? (
          <div className="py-4">
            <div className="flex justify-center mb-2">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
            <p className="text-sm font-medium text-slate-700">در حال فشرده‌سازی تصویر...</p>
            <p className="text-xs text-slate-500 mt-1">لطفا صبر کنید</p>
          </div>
        ) : isUploading ? (
          <div className="py-4">
            <div className="flex justify-between mb-1">
              <span className="text-sm font-medium text-slate-700">آپلود در حال انجام...</span>
              <span className="text-sm font-medium text-slate-700">{progress}%</span>
            </div>
            <div className="progress-container">
              <div className="progress-bar-bg">
                <div 
                  className={`progress-bar-fill progress-${Math.round(progress)}`}
                ></div>
              </div>
            </div>
          </div>
        ) : selectedFile ? (
          <div className="py-2 relative">
            {previewUrl ? (
              <div className="relative mx-auto w-32 h-32 mb-2">
                <img 
                  src={previewUrl} 
                  alt="Preview" 
                  className="w-full h-full object-cover rounded-md"
                />
                <button
                  type="button"
                  onClick={handleRemove}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600"
                >
                  ×
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-center bg-slate-100 rounded-md p-4 relative mx-auto w-32 mb-2">
                <span className="text-slate-700">{selectedFile.name.substring(0, 15)}...</span>
                <button
                  type="button"
                  onClick={handleRemove}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600"
                >
                  ×
                </button>
              </div>
            )}
            <p className="text-sm text-green-600">فایل با موفقیت آپلود شد</p>
          </div>
        ) : (
          <div className="py-6">
            <svg className="mx-auto h-12 w-12 text-slate-400" stroke="currentColor" fill="none" viewBox="0 0 48 48" aria-hidden="true">
              <path 
                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4h-8" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round" 
              />
            </svg>
            <p className="mt-2 text-sm text-slate-600">
              فایل را اینجا بکشید و رها کنید یا کلیک کنید
            </p>
            <p className="mt-1 text-xs text-slate-500">
               تا حداکثر 10MB
            </p>
            <p className="mt-1 text-xs text-slate-400">
              تصاویر به صورت خودکار فشرده‌سازی می‌شوند
            </p>
          </div>
        )}
      </div>
      
      {error && !isUploading && !isCompressing && (
        <p className="text-red-500 text-sm mt-1">{error}</p>
      )}
    </div>
  );
};

export default ModernFileUploader;
